import { useEffect, useRef } from 'react';
import TerrainMapPreview from '../TerrainMapPreview/TerrainMapPreview';
import type { MapListEntry } from '../../pages/setup-game.page';

interface MapSelectModalProps {
  maps: MapListEntry[];
  currentMapId: string;
  onSelect: (map: MapListEntry) => void;
  onClose: () => void;
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.65)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const MODAL_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--panel-bg)',
  color: 'var(--text-primary)',
  border: '1px solid var(--panel-border)',
  borderRadius: 8,
  maxWidth: 900,
  width: '100%',
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
};

const HEADER_STYLE: React.CSSProperties = {
  padding: '20px 24px 12px',
  borderBottom: '1px solid var(--panel-border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: 12,
  padding: 20,
  overflowY: 'auto',
  flex: 1,
};

const CARD_STYLE: React.CSSProperties = {
  border: '2px solid var(--panel-border)',
  borderRadius: 8,
  padding: 12,
  cursor: 'pointer',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: 'var(--panel-bg-raised)',
};

const CARD_SELECTED_STYLE: React.CSSProperties = {
  ...CARD_STYLE,
  borderColor: 'var(--p1)',
  boxShadow: '0 0 0 2px rgba(104, 184, 232, 0.25)',
};

export default function MapSelectModal({
  maps,
  currentMapId,
  onSelect,
  onClose,
}: MapSelectModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div ref={overlayRef} style={OVERLAY_STYLE} onClick={handleOverlayClick}>
      <div style={MODAL_STYLE}>
        <div style={HEADER_STYLE}>
          <h2 className="title is-5 mb-0">Select Map</h2>
          <button
            className="delete is-medium"
            onClick={onClose}
            type="button"
            aria-label="Close"
          />
        </div>
        <div style={GRID_STYLE}>
          {maps.map((map) => {
            const isSelected = map.id === currentMapId;
            return (
              <div
                key={map.id}
                style={isSelected ? CARD_SELECTED_STYLE : CARD_STYLE}
                onClick={() => onSelect(map)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      'var(--p1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      'var(--panel-border)';
                  }
                }}
              >
                <TerrainMapPreview
                  terrainMap={map.terrainMap}
                  cellSize={8}
                  ownerOverrides={map.ownerOverrides}
                  playerCount={map.playerCount}
                  highlightOwners
                />
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    marginTop: 10,
                    marginBottom: 3,
                    textAlign: 'center',
                    wordBreak: 'break-word',
                  }}
                >
                  {map.name}
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    marginBottom: 8,
                    textAlign: 'center',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.4,
                  }}
                >
                  {map.description}
                </p>
                <div className="tags is-justify-content-center mb-0">
                  <span className="tag is-info is-small">
                    {map.playerCount}p
                  </span>
                  <span className="tag is-light is-small">
                    {map.terrainMap.width}x{map.terrainMap.height}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
